// UTILS
import { nextTick } from "vue"
import { storeToRefs } from "pinia"
import { setup, createActor, fromPromise } from "xstate"
import router from "@/router"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"
import { useSessionStorage } from "@/helpers/useSessionStorage"

// TYPES
import { type GlobalMachineEvent } from "@/machines/types"
import type { DriveMachineEventType } from "./types"

// STORES
import { useUserStore } from "@/stores/user"
import { useAnswersStore } from "@/features/drive/store/answers.store"
import { useQuestionsStore } from "@/features/drive/store/questions.store"

// MACHINE
import { globalActor } from "@/machines/globalMachine"
import { useHelpStore } from "@/components/af/Help/help.store"

// Loader for Drive datas
// to redirect to Intro or WelcomeBack
const loadDriveData = fromPromise(async () => {
  const { fetchDrive } = useAnswersStore()
  const data = await fetchDrive()

  const { fetchQuestions, fetchAudios } = useQuestionsStore()
  const { questionsLength } = storeToRefs(useQuestionsStore())

  const assessmentSummary = useUserStore().assessmentSummary.assessments.drive
  const isFinisedAndNotPassed =
    data?.status === "finish" && assessmentSummary?.not_passed

  // Legacy, the test has been not_passed and considered with status finish in assessment summary (old) but not in workflow and next_assessment (new)
  let assessmentStatus = isFinisedAndNotPassed ? "todo" : data?.status

  // status can be "todo", "inprogress" or "finish"
  switch (assessmentStatus) {
    case "todo":
      await fetchQuestions()
      await fetchAudios()
      return { status: "new" }

    case "inprogress":
      await fetchQuestions()
      await fetchAudios()
      // If there is less answers than questions, we need to show the questions
      if (data.data.items.length < questionsLength.value) {
        return { status: "showQuestions" }
      }
      // If there is no native language, we need to show the native language screen
      else if (
        data.data.extra?.native_language == undefined ||
        data.data.extra?.native_language == null
      ) {
        return { status: "showNativeLanguage" }
      }
      // If there is no wholesome conditions, we need to show the wholesome conditions screen
      else if (
        data.data.extra?.wholesome_conditions == undefined ||
        data.data.extra?.wholesome_conditions == null
      ) {
        return { status: "showWholesomeConditions" }
      }
      // If the user is restarting the test, we need to show the restart screen
      else if (
        (data.data.extra?.native_language == false ||
          data.data.extra?.wholesome_conditions == false) &&
        (data.data.extra?.isRestarting == undefined ||
          data.data.extra?.isRestarting != true)
      ) {
        return { status: "showRestart" }
      }
      // If the user has complete every steps but status is inprogress, we need to send the results to backend
      else {
        return { status: "showLoadResults" }
      }

    case "redirect-contact":
      globalActor.send({ type: "GLOBAL.GUEST.CONTACTSUPPORT" })
      return

    case "thank-you":
      router.replace({ name: "thanksATS" })
      return

    case "finish":
      return { status: "showFinal" }

    default:
      await fetchQuestions()
      await fetchAudios()
      return { status: "new" }
  }
})

export const driveMachine = setup({
  actors: {
    loadDriveData,
  },
  types: {} as {
    events: DriveMachineEventType
  },
  actions: {},
}).createMachine({
  id: "driveMachine",
  initial: "loading",
  states: {
    loading: {
      // entry: [() => router.replace({ name: "driveLoading" })],
      entry: [
        () => {
          if (router.currentRoute.value.name !== "driveLoading") {
            router.replace({ name: "driveLoading" })
          }
        },
      ],
      invoke: {
        id: "loadDrive",
        src: "loadDriveData",
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "new",
            target: "intro",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showQuestions",
            target: "welcomeBack",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showNativeLanguage",
            target: "nativeLanguage",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showWholesomeConditions",
            target: "wholesomeConditions",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showRestart",
            target: "restart",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showLoadResults",
            target: "loadResults",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showFinal",
            target: "final",
          },
        ],
      },
    },
    intro: {
      entry: [() => router.replace({ name: "driveIntro" })],
      on: {
        done: { target: "rules" },
      },
    },
    welcomeBack: {
      entry: [() => router.replace({ name: "driveWelcomeBack" })],
      on: {
        done: { target: "test" },
      },
    },
    rules: {
      entry: [() => router.replace({ name: "driveRules" })],
      on: {
        done: { target: "practice" },
        back: { target: "intro" },
      },
    },
    practice: {
      entry: [() => router.replace({ name: "drivePractice" })],
      on: {
        done: { target: "tuto" },
        back: { target: "rules" },
      },
    },
    tuto: {
      entry: [() => router.replace({ name: "driveTuto" })],
      on: {
        done: { target: "tutoEnd" },
      },
    },
    tutoEnd: {
      entry: [() => router.replace({ name: "driveTutoEnd" })],
      on: {
        done: { target: "test" },
        back: { target: "tuto" },
      },
    },
    test: {
      entry: [() => router.replace({ name: "driveTest" })],
      on: {
        done: { target: "nativeLanguage" },
      },
    },
    nativeLanguage: {
      entry: [() => router.replace({ name: "driveNativeLanguage" })],
      on: {
        done: { target: "wholesomeConditions" },
      },
    },
    wholesomeConditions: {
      entry: [() => router.replace({ name: "driveWholesomeConditions" })],
      on: {
        true: { target: "loadResults" },
        false: { target: "restart" },
      },
    },
    restart: {
      entry: [() => router.replace({ name: "driveRestart" })],
      on: {
        now: { target: "loading" },
        done: { target: "loadResults" },
      },
    },
    loadResults: {
      entry: [() => router.replace({ name: "driveLoadResults" })],
      on: {
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export const driveActor = createActor(driveMachine)

driveActor.subscribe({
  async complete() {
    const { isFromInt } = storeToRefs(useUserStore())
    const { getSessionItem } = useSessionStorage()
    const { fetchNextAssessment } = useUserStore()
    const { nextAssessmentName } = storeToRefs(useUserStore())
    const { af_workflow_uuid, af_retake } = storeToRefs(useHelpStore())

    const retake = af_retake.value ?? useCookies().get(COOKIES.RETAKE)
    await fetchNextAssessment(af_workflow_uuid.value ?? null, retake ?? false)

    await nextTick()

    if (isFromInt.value && getSessionItem("goto_param")) {
      const didRedirect = await redirectForInt()
      if (didRedirect) return
    }

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.WORKFLOW_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.PM_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.COMPANY_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.RETAKE, COOKIES.OPTIONS)
      af_workflow_uuid.value = ""
      af_retake.value = false

      const didRedirect = await redirectForInt()
      if (didRedirect) return

      globalActor.send({ type: "GLOBAL.DRIVE.DONE" })
    }
  },
})

async function redirectForInt(): Promise<boolean> {
  const { isFromInt } = storeToRefs(useUserStore())
  const { getSessionItem, deleteSessionItem } = useSessionStorage()

  if (isFromInt.value) {
    if (getSessionItem("goto_param")) {
      deleteSessionItem("goto_param")
    }

    if (getSessionItem("redirect_url")) {
      let redirectUrl = getSessionItem("redirect_url") as string
      deleteSessionItem("redirect_url")
      window.location.href = redirectUrl
      return true
    } else {
      await router.push({ name: "thanksATS" })
      return true
    }
  }

  return false
}
